/**
 * お会計 state (T07-24, design/05 §3.2). The cashier picks how the customer pays — 現金 / カード
 * (EDC) / ストアクレジット / 分割 — and every number on the screen (残額, お釣り, shortcut buttons,
 * the card fee for managers, the 印紙税 notice) comes from pos-service's preview
 * (`POST /pos/carts/{id}/tender-preview`); the tablet only turns typed digits into integers.
 *
 * The sale is `POST /pos/orders` with an `Idempotency-Key` made when the screen opened: a retry
 * (lost answer, "tap again to continue") sends the same key → never charged twice; the button
 * is off while it runs, so tapping it five times sends one request.
 *
 * Store credit is the basket customer's prepaid money: until a customer-presence check exists the
 * server answers `403 manager_approval_required` (`sale.store_credit`) → the manager confirms the
 * customer is there (PIN) → the same sale is sent again with `X-Approval-Token` (same key).
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useApproval } from "@/app/approval";
import { usePos } from "@/app/context";
import { call } from "@/lib/api";
import { ApiError } from "@/lib/errors";
import { uuidv7 } from "@/lib/idempotency";
import type {
  Cart,
  PaymentMethods,
  Sale,
  SaleCreate,
  SaleTender,
  TenderDraft,
  TenderPreview,
} from "@/lib/types";

export type TenderMode = "cash" | "card" | "credit" | "split";
export type PartMethod = "cash" | "edc_external" | "store_credit";

export interface TenderPart {
  id: string;
  method: PartMethod;
  /** Digits typed (or copied from the server's numbers) — never computed here. */
  amount: string;
  /** Cash: お預かり ("" = exactly the amount). */
  tendered: string;
  terminalId: string;
  cardBrand: string;
  approvalCode: string;
  last4: string;
}

export interface ReceiptChoice {
  documentType: "kani_invoice" | "tekikaku_invoice";
  recipient: string;
  print: boolean;
  email: boolean;
  emailTo: string;
  qr: boolean;
  /** 印紙税 (T13-24): the cashier will put the ¥200 stamp on this paper receipt (Z-report count) */
  stampAffixed: boolean;
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Digits → integer yen (0 when empty). The only number handling on this screen. */
export function yen(digits: string): number {
  const value = Number.parseInt(digits, 10);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

let partSeq = 0;
function part(method: PartMethod, amount: number | null | undefined, terminalId = ""): TenderPart {
  partSeq += 1;
  return {
    id: `p${String(partSeq)}`,
    method,
    amount: amount ? String(amount) : "",
    tendered: "",
    terminalId,
    cardBrand: "",
    approvalCode: "",
    last4: "",
  };
}

export function toDraft(p: TenderPart): TenderDraft {
  if (p.method === "cash") {
    return {
      method: "cash",
      amount: yen(p.amount),
      ...(p.tendered ? { tendered: yen(p.tendered) } : {}),
    };
  }
  if (p.method === "edc_external") {
    return {
      method: "edc_external",
      amount: yen(p.amount),
      ...(p.terminalId ? { terminalId: p.terminalId } : {}),
      ...(p.cardBrand ? { cardBrand: p.cardBrand } : {}),
    };
  }
  return { method: "store_credit", amount: yen(p.amount) };
}

export function toTender(p: TenderPart): SaleTender {
  if (p.method === "cash") {
    return {
      method: "cash",
      amount: yen(p.amount),
      tendered: p.tendered ? yen(p.tendered) : yen(p.amount),
    };
  }
  if (p.method === "edc_external") {
    return {
      method: "edc_external",
      amount: yen(p.amount),
      terminalId: p.terminalId,
      cardBrand: p.cardBrand,
      approvalCode: p.approvalCode,
      ...(p.last4 ? { last4: p.last4 } : {}),
    };
  }
  return { method: "store_credit", amount: yen(p.amount) };
}

/** Is this part filled in enough to be sent (the server checks the money itself)? */
export function partReady(p: TenderPart): boolean {
  if (yen(p.amount) <= 0) {
    return false;
  }
  if (p.method === "edc_external") {
    return (
      Boolean(p.terminalId) &&
      Boolean(p.cardBrand) &&
      /^[0-9A-Za-z]{1,20}$/.test(p.approvalCode) &&
      (p.last4 === "" || /^[0-9]{4}$/.test(p.last4))
    );
  }
  return true;
}

export interface UseTenderOptions {
  cart: Cart;
  methods: PaymentMethods | undefined;
  initialMode: TenderMode;
  defaultDocumentType: string;
  defaultPrint: boolean;
  paperWidth: 58 | 80;
  /** 免税 (T07-30): the buyer's language for the receipt's second lines + the 案内. */
  customerLocale?: SaleCreate["locale"];
  /** 免税 (T07-30): the buyer's e-mail typed in the wizard. */
  initialEmail?: string;
}

export function useTender(options: UseTenderOptions) {
  const { cart, methods, initialMode, defaultDocumentType, defaultPrint, paperWidth } = options;
  // a 免税 sale always prints (the receipt carries 「免税」 / the 90-day notice — T07-30)
  const taxFree = Boolean(cart.taxFree);
  const { api } = usePos();
  const { withApproval } = useApproval();
  const { i18n } = useTranslation();
  const total = cart.totals.total;
  const firstTerminal = useMemo(
    () => methods?.data.find((m) => m.method === "edc_external")?.terminal?.id ?? "",
    [methods],
  );

  const initialParts = useCallback(
    (mode: TenderMode): TenderPart[] => {
      if (mode === "card") {
        return [part("edc_external", total, firstTerminal)];
      }
      if (mode === "credit") {
        return [part("store_credit", null)];
      }
      if (mode === "split") {
        return [];
      }
      return [part("cash", total)];
    },
    [firstTerminal, total],
  );

  const [mode, setModeState] = useState<TenderMode>(initialMode);
  const [parts, setParts] = useState<TenderPart[]>(() => initialParts(initialMode));
  const [receipt, setReceipt] = useState<ReceiptChoice>({
    documentType: defaultDocumentType === "tekikaku_invoice" ? "tekikaku_invoice" : "kani_invoice",
    recipient: "",
    print: defaultPrint || taxFree,
    email: Boolean(options.initialEmail),
    emailTo: options.initialEmail ?? cart.customer?.email ?? "",
    qr: false,
    stampAffixed: false,
  });
  // one key for this お会計 — a retry of the same sale reuses it
  const [idempotencyKey] = useState(uuidv7);
  const [submitting, setSubmitting] = useState(false);
  const inflight = useRef<Promise<Sale> | null>(null);

  // what the cashier has not chosen yet takes the defaults the server gave: the first card
  // terminal, and for store credit the amount the server says can be used
  const [usable, setUsable] = useState<number | null>(null);
  const effective = useMemo(
    () =>
      parts.map((p) => ({
        ...p,
        terminalId: p.method === "edc_external" && !p.terminalId ? firstTerminal : p.terminalId,
        amount:
          p.method === "store_credit" && p.amount === "" && usable && usable > 0
            ? String(usable)
            : p.amount,
      })),
    [parts, firstTerminal, usable],
  );
  const drafts = useMemo(() => effective.map(toDraft), [effective]);
  const [debounced, setDebounced] = useState(drafts);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(drafts), 120);
    return () => clearTimeout(timer);
  }, [drafts]);

  const preview = useQuery({
    queryKey: [
      "pos-tender-preview",
      cart.id,
      cart.version,
      JSON.stringify(debounced),
      receipt.print,
    ],
    queryFn: () =>
      call(() =>
        api.POST("/v1/pos/carts/{cart_id}/tender-preview", {
          params: { path: { cart_id: cart.id } },
          body: { payments: debounced, print: receipt.print },
        }),
      ),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const fresh = JSON.stringify(debounced) === JSON.stringify(drafts) && !preview.isPlaceholderData;
  const view: TenderPreview | undefined = preview.data;
  const serverUsable = view?.storeCreditUsable ?? null;
  if (serverUsable !== usable) {
    setUsable(serverUsable); // derived from the latest preview (render-time update, no effect)
  }

  const setMode = (next: TenderMode) => {
    setModeState(next);
    if (next === "split") {
      // keep what was typed; the cashier adds the next way of paying
      return;
    }
    setParts(initialParts(next));
  };

  const update = (id: string, changes: Partial<TenderPart>) =>
    setParts((list) => list.map((p) => (p.id === id ? { ...p, ...changes } : p)));

  /** 分割: add a way of paying for what is still open (the server's 残額). */
  const addPart = (method: PartMethod) => {
    const remaining = view?.remaining ?? total;
    const amount = method === "store_credit" ? (view?.storeCreditUsable ?? null) : remaining;
    setParts((list) => [
      ...list,
      part(
        method,
        amount && amount > 0 ? amount : null,
        method === "edc_external" ? firstTerminal : "",
      ),
    ]);
  };

  const removePart = (id: string) => setParts((list) => list.filter((p) => p.id !== id));

  const receiptReady =
    (receipt.documentType !== "tekikaku_invoice" || receipt.recipient.trim().length > 0) &&
    (!receipt.email || EMAIL.test(receipt.emailTo.trim()));
  const canSubmit =
    !submitting &&
    fresh &&
    Boolean(view?.complete) &&
    effective.length > 0 &&
    effective.every(partReady) &&
    receiptReady;

  const submit = async (): Promise<Sale> => {
    if (inflight.current) {
      return inflight.current; // a second tap while sending: the same request
    }
    const body: SaleCreate = {
      cartId: cart.id,
      payments: effective.map(toTender),
      documentType: receipt.documentType,
      ...(receipt.documentType === "tekikaku_invoice"
        ? { invoiceRecipientName: receipt.recipient.trim() }
        : {}),
      locale:
        options.customerLocale ??
        ((["ja", "th", "en"].includes(i18n.resolvedLanguage ?? "")
          ? i18n.resolvedLanguage
          : "ja") as SaleCreate["locale"]),
      receiptOptions: {
        print: receipt.print || taxFree,
        email: receipt.email ? receipt.emailTo.trim() : null,
        paperWidth,
        stampAffixed: receipt.stampAffixed && (receipt.print || taxFree),
      },
    };
    setSubmitting(true);
    const send = (approvalToken?: string) =>
      call(() =>
        api.POST("/v1/pos/orders", {
          body,
          headers: {
            "Idempotency-Key": idempotencyKey,
            ...(approvalToken ? { "X-Approval-Token": approvalToken } : {}),
          },
        }),
      );
    const run = withApproval(send, { subject: cart.id, amount: total });
    inflight.current = run;
    try {
      return await run;
    } finally {
      inflight.current = null;
      setSubmitting(false);
    }
  };

  return {
    mode,
    setMode,
    parts: effective,
    update,
    addPart,
    removePart,
    receipt,
    setReceipt,
    preview: view,
    previewError: preview.error instanceof ApiError ? preview.error : null,
    fresh,
    total,
    canSubmit,
    submitting,
    submit,
    idempotencyKey,
  };
}

export type TenderState = ReturnType<typeof useTender>;
